import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Dialog, IconButton, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ImagesContext from "../contexts/ImagesContext";

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: theme.spacing(2),
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    },
  },
  tile: {
    position: 'relative',
    display: 'block',
    width: '100%',
    padding: 0,
    border: 0,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    cursor: 'zoom-in',
    backgroundColor: '#EDEAE3',
    aspectRatio: '1 / 1',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(180deg, transparent 55%, rgba(20,33,46,0.35))',
      opacity: 0,
      transition: 'opacity 200ms ease',
    },
    '&:hover img, &:focus-visible img': {
      transform: 'scale(1.05)',
    },
    '&:hover::after, &:focus-visible::after': {
      opacity: 1,
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
  },
  thumb: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'transform 400ms ease',
  },
  dialogPaper: {
    backgroundColor: '#0B131B',
    margin: theme.spacing(2),
    maxWidth: 'calc(100% - 32px)',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      maxWidth: '100%',
    },
  },
  viewer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    height: '100%',
  },
  fullImage: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 64px)',
    objectFit: 'contain',
    [theme.breakpoints.down('xs')]: {
      maxHeight: '100vh',
    },
  },
  control: {
    position: 'absolute',
    color: '#FFFFFF',
    backgroundColor: 'rgba(0,0,0,0.45)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.65)',
    },
  },
  close: {
    top: 12,
    right: 12,
  },
  prev: {
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  next: {
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  counter: {
    position: 'absolute',
    bottom: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#FFFFFF',
    backgroundColor: 'rgba(0,0,0,0.45)',
    padding: '2px 12px',
    borderRadius: 999,
  },
}));

export default function Gallery() {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [images, setImages] = useState([]);
  const [openIndex, setOpenIndex] = useState(-1);
  const imagesContext = useContext(ImagesContext);
  const { imageURLs } = imagesContext;

  useEffect(() => {
    setImages(imageURLs);
  }, [imageURLs]);

  const close = () => setOpenIndex(-1);

  const step = useCallback((delta) => {
    setOpenIndex((index) => (index + delta + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (openIndex < 0) return undefined;
    const onKey = (event) => {
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, step]);

  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <div className={classes.grid}>
        {images.map((url, index) => (
          <button
            type="button"
            key={url}
            className={classes.tile}
            onClick={() => setOpenIndex(index)}
            aria-label={`Open project photo ${index + 1} of ${images.length}`}>
            <img
              src={url}
              alt={`Project ${index + 1}`}
              loading="lazy"
              className={classes.thumb}
              onError={() => setImages((list) => list.filter((item) => item !== url))}
            />
          </button>
        ))}
      </div>

      <Dialog
        open={openIndex >= 0 && openIndex < images.length}
        onClose={close}
        fullScreen={fullScreen}
        maxWidth={false}
        classes={{ paper: classes.dialogPaper }}
        aria-label="Project photo viewer">
        {openIndex >= 0 && openIndex < images.length && (
          <div className={classes.viewer}>
            <img
              src={images[openIndex]}
              alt={`Project ${openIndex + 1}`}
              className={classes.fullImage}
            />
            <IconButton className={`${classes.control} ${classes.close}`} onClick={close} aria-label="Close">
              <CloseIcon />
            </IconButton>
            {images.length > 1 && (
              <>
                <IconButton className={`${classes.control} ${classes.prev}`} onClick={() => step(-1)} aria-label="Previous photo">
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton className={`${classes.control} ${classes.next}`} onClick={() => step(1)} aria-label="Next photo">
                  <ChevronRightIcon />
                </IconButton>
                <Typography variant="body2" className={classes.counter}>
                  {openIndex + 1} / {images.length}
                </Typography>
              </>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}
